import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Camera, Save, User, MapPin, Phone, Building, CreditCard, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_STATUS_OPTIONS = [
  { value: 'citizen', label: 'Citizen' },
  { value: 'permanent_resident', label: 'Permanent Resident' },
  { value: 'work_permit', label: 'Work Permit' },
  { value: 'student_visa', label: 'Student Visa' }
];

const PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
  'Newfoundland and Labrador', 'Nova Scotia', 'Ontario',
  'Prince Edward Island', 'Quebec', 'Saskatchewan',
  'Northwest Territories', 'Nunavut', 'Yukon'
];

export function PersonalDetailsDialog({ open, onOpenChange, user, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    
    // Address
    address: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'Canada',
    
    // Contact
    contact_phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    
    // Legal & Financial
    legal_status: '',
    bank_institution: '',
    bank_transit: '',
    bank_account: '',
    hst_number: '',
    
    // Profile Picture
    profile_picture_url: ''
  });

  useEffect(() => {
    if (open) {
      fetchPersonalDetails();
    }
  }, [open]);

  const fetchPersonalDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Session expired. Please log in again.');
        setLoading(false);
        return;
      }
      
      if (!API_URL) {
        toast.error('Configuration error. Please refresh the page.');
        setLoading(false);
        return;
      }
      
      console.log('[PersonalDetails] Fetching from:', `${API_URL}/api/my-profile/personal-details`);
      
      const response = await fetch(`${API_URL}/api/my-profile/personal-details`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[PersonalDetails] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[PersonalDetails] Data loaded successfully');
        setFormData(prev => ({
          ...prev,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          ...(data.personal_details || {})
        }));
      } else {
        const errorText = await response.text();
        console.error('[PersonalDetails] Server error:', response.status, errorText);
        if (response.status === 401) {
          toast.error('Session expired. Please log in again.');
        } else if (response.status === 404) {
          toast.error('API endpoint not found. Please contact support.');
        } else {
          toast.error(`Server error (${response.status}). Please try again.`);
        }
      }
    } catch (error) {
      console.error('[PersonalDetails] Fetch error:', error);
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        toast.error('Cannot connect to server. Please check your connection.');
      } else {
        toast.error(`Error loading details: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!API_URL) {
        toast.error('API URL not configured');
        return;
      }
      
      if (!token) {
        toast.error('Please log in again');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/my-profile/personal-details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postal_code: formData.postal_code,
          country: formData.country,
          contact_phone: formData.contact_phone,
          emergency_contact_name: formData.emergency_contact_name,
          emergency_contact_phone: formData.emergency_contact_phone,
          legal_status: formData.legal_status,
          bank_institution: formData.bank_institution,
          bank_transit: formData.bank_transit,
          bank_account: formData.bank_account,
          hst_number: formData.hst_number,
          date_of_birth: formData.date_of_birth
        })
      });

      if (response.ok) {
        toast.success('Personal details saved successfully');
        onUpdate?.();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save failed:', response.status, errorData);
        toast.error(errorData.detail || `Save failed (${response.status})`);
      }
    } catch (error) {
      console.error('Profile save error:', error.message, error);
      // Show more specific error message
      if (error.message?.includes('Failed to fetch')) {
        toast.error('Cannot connect to server. Please check your internet connection.');
      } else if (error.message?.includes('NetworkError')) {
        toast.error('Network error. Please try again.');
      } else {
        toast.error(`Connection error: ${error.message || 'Please try again'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/my-profile/profile-picture`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, profile_picture_url: data.url }));
        toast.success('Profile picture updated');
        onUpdate?.();
      } else {
        toast.error('Failed to upload photo');
      }
    } catch (error) {
      toast.error('Error uploading photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            My Profile - Personal Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profile Picture Section */}
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="relative">
              {formData.profile_picture_url ? (
                <img 
                  src={`${API_URL}${formData.profile_picture_url}`}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border-2 border-indigo-200"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="w-10 h-10 text-indigo-400" />
                </div>
              )}
              <button
                onClick={triggerFileInput}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 bg-indigo-600 text-white p-1.5 rounded-full hover:bg-indigo-700 disabled:opacity-50"
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{formData.name}</h3>
              <p className="text-sm text-gray-500">{formData.email}</p>
              <p className="text-xs text-gray-400 mt-1">Tap camera to take/upload photo</p>
            </div>
          </div>

          {/* Tabs for different sections */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="basic" className="text-xs sm:text-sm">
                <User className="w-4 h-4 mr-1 hidden sm:inline" />
                Basic
              </TabsTrigger>
              <TabsTrigger value="address" className="text-xs sm:text-sm">
                <MapPin className="w-4 h-4 mr-1 hidden sm:inline" />
                Address
              </TabsTrigger>
              <TabsTrigger value="contact" className="text-xs sm:text-sm">
                <Phone className="w-4 h-4 mr-1 hidden sm:inline" />
                Contact
              </TabsTrigger>
              <TabsTrigger value="financial" className="text-xs sm:text-sm">
                <CreditCard className="w-4 h-4 mr-1 hidden sm:inline" />
                Financial
              </TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input value={formData.name} disabled className="bg-gray-100" />
                  <p className="text-xs text-gray-400 mt-1">Contact admin to change name</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={formData.email} disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input value={formData.phone} disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input 
                    type="date" 
                    value={formData.date_of_birth || ''} 
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    data-testid="dob-input"
                  />
                </div>
                <div>
                  <Label>Legal Status</Label>
                  <Select 
                    value={formData.legal_status || ''} 
                    onValueChange={(v) => handleChange('legal_status', v)}
                  >
                    <SelectTrigger data-testid="legal-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEGAL_STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.legal_status && formData.legal_status !== 'citizen' && (
                  <div className="md:col-span-2">
                    <Label>Legal Status Document</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Upload your {formData.legal_status === 'work_permit' ? 'Work Permit' : 
                                   formData.legal_status === 'permanent_resident' ? 'PR Card' : 
                                   formData.legal_status === 'student_visa' ? 'Study Permit' : 'document'}
                    </p>
                    {formData.legal_status_document_url ? (
                      <a 
                        href={`${API_URL}${formData.legal_status_document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View Uploaded Document
                      </a>
                    ) : (
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200">
                        <Camera className="w-4 h-4" />
                        Upload Document (PDF, JPG, PNG)
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,image/*"
                          capture="environment"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const formDataUpload = new FormData();
                              formDataUpload.append('file', file);
                              formDataUpload.append('document_type', 'legal_status');
                              try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${API_URL}/api/my-profile/legal-status/upload?document_type=legal_status`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formDataUpload
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setFormData(prev => ({ ...prev, legal_status_document_url: data.document_url }));
                                  toast.success('Legal document uploaded');
                                }
                              } catch (error) {
                                toast.error('Failed to upload document');
                              }
                            }
                          }}
                          data-testid="legal-doc-upload"
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Address Tab */}
            <TabsContent value="address" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Street Address</Label>
                  <Input 
                    value={formData.address || ''} 
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="123 Main Street, Unit 4"
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input 
                      value={formData.city || ''} 
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Toronto"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label>Province</Label>
                    <Select 
                      value={formData.province || ''} 
                      onValueChange={(v) => handleChange('province', v)}
                    >
                      <SelectTrigger data-testid="province-select">
                        <SelectValue placeholder="Select province" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVINCES.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Postal Code</Label>
                    <Input 
                      value={formData.postal_code || ''} 
                      onChange={(e) => handleChange('postal_code', e.target.value.toUpperCase())}
                      placeholder="M5V 1A1"
                      maxLength={7}
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input 
                      value={formData.country || 'Canada'} 
                      onChange={(e) => handleChange('country', e.target.value)}
                      data-testid="country-input"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Alternate Phone</Label>
                  <Input 
                    value={formData.contact_phone || ''} 
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="(416) 555-0123"
                    data-testid="alt-phone-input"
                  />
                </div>
              </div>
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-orange-700">Emergency Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Emergency Contact Name</Label>
                    <Input 
                      value={formData.emergency_contact_name || ''} 
                      onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
                      placeholder="John Doe"
                      data-testid="emergency-name-input"
                    />
                  </div>
                  <div>
                    <Label>Emergency Contact Phone</Label>
                    <Input 
                      value={formData.emergency_contact_phone || ''} 
                      onChange={(e) => handleChange('emergency_contact_phone', e.target.value)}
                      placeholder="(416) 555-0123"
                      data-testid="emergency-phone-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Financial Tab */}
            <TabsContent value="financial" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Bank Account Details
                  </CardTitle>
                  <p className="text-xs text-gray-500">For direct deposit payments</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Bank/Institution Name</Label>
                    <Input 
                      value={formData.bank_institution || ''} 
                      onChange={(e) => handleChange('bank_institution', e.target.value)}
                      placeholder="TD Canada Trust"
                      data-testid="bank-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Transit Number</Label>
                      <Input 
                        value={formData.bank_transit || ''} 
                        onChange={(e) => handleChange('bank_transit', e.target.value)}
                        placeholder="12345"
                        maxLength={5}
                        data-testid="bank-transit-input"
                      />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input 
                        value={formData.bank_account || ''} 
                        onChange={(e) => handleChange('bank_account', e.target.value)}
                        placeholder="1234567"
                        data-testid="bank-account-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Tax Information (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label>HST Number</Label>
                    <Input 
                      value={formData.hst_number || ''} 
                      onChange={(e) => handleChange('hst_number', e.target.value)}
                      placeholder="123456789RT0001"
                      data-testid="hst-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">Only if you're a registered contractor</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} data-testid="save-details-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PersonalDetailsDialog;
